import type { ReferralSourceAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import { COLLEGE_BOARD_PARTNER_SLUG } from './constants'
import UserQ from './UserQ'

const ReferralSourceQ = {
  ...makeQueryModelV2<ReferralSourceAttributes>('referral_source')([
    'id',
    'user_id',
    'referral_code',
    'partner_slug',
    'description',
    'details',
    'created_at',
    'slug',
    'header',
    'subheader',
    'logo_url',
  ]),
  associations: (): AssociationDefinitions => ({
    Users: {
      query: UserQ,
      foreignKey: UserQ.referral_source_id,
      sourceKey: ReferralSourceQ.id,
    },
  }),

  isFromCollegeBoard: (): SQLFragment<boolean> =>
    SQL`${ReferralSourceQ.partner_slug} = ${COLLEGE_BOARD_PARTNER_SLUG}`,

  where: (
    {
      id,
      slug,
      partnerSlug,
      userId,
      referralCode,
      include = [],
      groupBy = null,
      order = null,
    }: {
      id?: number | null | SQLFragment<number>
      slug?: string | null | SQLFragment<string>
      partnerSlug?: string | null | SQLFragment<string>
      userId?: number | null | SQLFragment<number>
      referralCode?: string | null | SQLFragment<string>
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<ReferralSourceAttributes> => SQL`
      FROM ${ReferralSourceQ}
      ${includeAssociationsToSql(ReferralSourceQ, include)}
      WHERE
          ${id != null ? SQL`${ReferralSourceQ.id} = ${id} AND` : SQL``}
          ${slug != null ? SQL`${ReferralSourceQ.slug} = ${slug} AND` : SQL``}
          ${partnerSlug != null ? SQL`${ReferralSourceQ.partner_slug} = ${partnerSlug} AND` : SQL``}
          ${userId != null ? SQL`${ReferralSourceQ.user_id} = ${userId} AND` : SQL``}
          ${
            referralCode != null
              ? SQL`${ReferralSourceQ.referral_code} = ${referralCode} AND`
              : SQL``
          }
          ${customWhere != null ? customWhere : SQL`TRUE`}
      ${queryGroupByToSQL(groupBy)}
      ${queryOrderToSQL(order)}
  `,

  getUsersWithMostReferrals: ({
    limit,
    referringUsersFilter,
    referredUsersFilter,
  }: {
    limit: number
    referringUsersFilter?: SQLFragment<unknown> | null
    referredUsersFilter?: SQLFragment<unknown> | null
  }): SQLFragment<number> => SQL`
        FROM ${ReferralSourceQ} JOIN
        (SELECT ${UserQ.referral_source_id}, COUNT(${
          UserQ.referral_source_id
        }) as statistic FROM ${UserQ}
            WHERE ${UserQ.referral_source_id} IS NOT NULL
            AND ${UserQ.hasBeenApproved()}
            ${referredUsersFilter == null ? SQL`` : SQL`AND ${referredUsersFilter}`}
            GROUP BY ${UserQ.referral_source_id}) as refs
        ON refs.referral_source_id = ${ReferralSourceQ.id}
        JOIN ${UserQ} ON ${UserQ.id} = ${ReferralSourceQ.user_id}
        ${referringUsersFilter == null ? SQL`` : SQL`WHERE ${referringUsersFilter}`}
        ORDER BY statistic DESC, ${UserQ.id} ASC
        LIMIT ${limit}
    `,
}

export default ReferralSourceQ
